import React, { useState } from "react";

import { Button } from "app/components/ui/button";

const faqData = [
  {
    id: 1,
    question: "Which countries can I call with my VLE Mobile plan?",
    answer:
      "All our plans include free international long distance calling to over 170 countries worldwide! Whether you're calling family in Mexico, friends in India, or business contacts in Europe, your calls are included at no extra charge. Just dial like you would any international number.",
  },
  {
    id: 2,
    question: "Do I need to add anything special to make international calls?",
    answer:
      "Nope! International calling to 170+ countries is already built into every VLE Mobile plan. There's no need to purchase add-ons, activate special features, or dial confusing codes. Simply make your call, and you're good to go.",
  },
  {
    id: 3,
    question: "Will international calls use my data or minutes?",
    answer:
      "International calls don't use your data at all, they're included as part of your unlimited voice calling. You can talk as long as you want to supported countries without worrying about extra charges or eating into your high-speed data.",
  },
  {
    id: 4,
    question: "Can I use my phone when traveling outside the United States?",
    answer:
      "Absolutely! Our Dual SIM/eSIM plans (3GB, 5GB, 15GB, and Unlimited) include free data roaming in over 80 countries at the same high speeds you enjoy at home. Single Network plans include roaming in Canada and Mexico at 3G speed. Stay connected wherever your travels take you!",
  },
  {
    id: 5,
    question: "Will my data slow down when I'm traveling internationally?",
    answer:
      "Not at all! When you travel to any of our 80+ supported countries with our Triple Network plans, you'll enjoy the same high speed 4G and 5G data as you do in the United States. However, our single network plans include complementary data roaming in Mexico and Canada at 3G speeds.",
  },
  {
    id: 6,
    question: "Do I need to buy a separate international roaming plan?",
    answer:
      "No way! International data roaming is already included in your plan at no additional cost. With our Dual SIM plans, you automatically get roaming in 80+ countries. With Single Network plans, you're covered in Canada and Mexico. No need to purchase expensive travel packages or roaming add-ons.",
  },
  {
    id: 7,
    question: "What happens to my unused data when I travel abroad?",
    answer:
      "Your unused US data will carry over to the next month with our Triple Network plans (3GB, 5GB, and 15GB). However, roaming data used while traveling internationally does not carry over. Only the high-speed data you use within the United States is eligible for our unlimited data carryover feature.",
  },
  {
    id: 8,
    question: "How much roaming data is included?",
    answer:
      "The amount of roaming data depends on your plan. Our Single Network plans include 5GB of roaming data at 3G speeds in Mexico and Canada. For Multi Network plans, roaming data is distributed as follows: the 3GB plan includes 1GB of roaming data, the 5GB plan includes 3GB of roaming data, the 15GB plan includes 5GB of roaming data, and the Unlimited plan includes 5GB of roaming data for use in 80+ countries.",
  },
  {
    id: 9,
    question: "Can I make international calls while I'm traveling overseas?",
    answer:
      "When traveling in Mexico and Canada, you can make calls up to 50 minutes and send up to 50 SMS. However, you can use unlimited Wi-Fi calling from anywhere in the world to stay connected for no extra charge! Simply connect to Wi-Fi and make calls to over 170 countries just like you would at home.",
  },
  {
    id: 10,
    question: "Are there any hidden fees for using my phone internationally?",
    answer:
      "Zero hidden fees! International calling to 170+ countries and international roaming (80+ countries for Triple Network plans, Canada/Mexico for Single Network plans) are included in your monthly price. What you see is what you pay, no surprise charges when you travel or call abroad.",
  },
];

export const Faq = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleFaqToggle = (faqId: number) => {
    setOpenFaq(openFaq === faqId ? null : faqId);
  };

  return (
    <section className="px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12 pb-14 sm:pb-[4.5rem] md:pb-[5.5rem] lg:pb-[7.5rem]">
      <div className="w-full max-w-[65.75rem] mx-auto">
        <div className="mb-6 text-center sm:mb-9 md:mb-12 lg:mb-14 2xl:mb-[3.75rem]">
          <h2 className="font-mobile text-[#1D1D1B] text-[clamp(1.5rem,calc(2.5vw+1rem),3.125rem)] font-normal leading-snug md:leading-normal lg:leading-[5.25rem]">
            Frequently Asked Questions
          </h2>
          <p className="text-[#1D1D1B] text-[0.925rem] sm:text-base lg:text-lg font-normal leading-5 sm:leading-6 lg:leading-7">
            Find answers to common questions about our service
          </p>
        </div>
        <ul className="mx-auto space-y-4 w-full sm:space-y-5 lg:space-y-6">
          {faqData.map((faq, index) => (
            <li
              key={index}
              className={`pt-0 overflow-hidden ${
                index === faqData.length - 1
                  ? "border-b-0"
                  : "pb-5 sm:pb-7 lg:pb-9 border-b border-[#E4E7EC]"
              }`}
            >
              <Button
                type="button"
                variant="ghost"
                className="gap-4 justify-between w-full text-left rounded-none transition-colors duration-100 hover:bg-transparent sm:gap-6"
                onClick={() => handleFaqToggle(faq.id)}
                aria-expanded={openFaq === faq.id}
                aria-controls={`faq-content-${faq.id}`}
              >
                <h4 className="font-mobile text-[#101828] text-[0.925rem] sm:text-base lg:text-lg font-medium leading-5 sm:leading-6 lg:leading-7 flex-1 min-w-0">
                  <span className="whitespace-normal break-words line-clamp-2">
                    {faq.question}
                  </span>
                </h4>
                <div className="shrink-0">
                  <img
                    src={
                      openFaq === faq.id
                        ? "/assets/minus-circle.svg"
                        : "/assets/plus-circle.svg"
                    }
                    alt={openFaq === faq.id ? "Collapse" : "Expand"}
                    className="size-5 sm:size-[1.375rem] lg:size-6 transition-transform duration-100 ease-in-out"
                  />
                </div>
              </Button>
              <div
                id={`faq-content-${faq.id}`}
                className={`overflow-y-auto transition-all duration-100 ease-in-out ${
                  openFaq === faq.id
                    ? "max-h-96 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <p className="text-[#667085] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 pt-1 sm:pt-1.5 lg:pt-2">
                  {faq.answer}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
